import { FC, ReactNode, useState } from 'react';
import { IconName, Icon } from '../Icon';
import DropdownActionTopbar from './DropdownActionTopbar';
import Tooltip from '../Tooltip';
import SearchInput from '../SearchInput';
import { LoadingOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { getTheme } from 'Store/MultiDomain/MultiDomain.selector';
import AiButton from 'Pages/Activities/Modals/AiChatModal/AiButton';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import SpinCustom from '../SpinCustom';
import { TableActionTopbarButtonWrapper, TopbarWrapper } from './styles';

export interface ITableActionTopbarButton {
  icon?: IconName;
  iconCustomSize?: number;
  description?: string;
  disabled?: boolean;
  hidden?: boolean;
  dropdownMenu?: any;
  isLoading?: boolean;
  tooltipMessage?: string;
  iconLoading?: boolean;
  isBlocked?: boolean;
  showArrow?: boolean;
  iconComponent?: ReactNode;
  action?: () => void;
}

export interface ITableActionTopbar {
  buttons: ITableActionTopbarButton[];
  totalSelectedElements?: number;
  inputPlaceholder?: string;
  inputClassName?: string | null;
  defaultValue?: string;
  LeftComponent?: ReactNode;
  showFilters?: boolean;
  onSearch?: (value: string) => void;
  onSearchLoading?: (value: boolean) => void;
  toggleAiModal?: () => void;
}

const TableActionTopbar: FC<ITableActionTopbar> = ({
  buttons,
  totalSelectedElements,
  inputPlaceholder,
  inputClassName,
  defaultValue,
  LeftComponent,
  showFilters,
  onSearch,
  onSearchLoading,
  toggleAiModal,
}) => {
  const theme = useSelector(getTheme);
  const activityAIChat = useFeatureFlag('activity-ai-chat');

  const [displayDropdownButtons, setDisplayDropdownButtons] = useState<boolean[]>([]);

  const toggleDropdown = (index: number) => {
    displayDropdownButtons[index] = !displayDropdownButtons[index];
    setDisplayDropdownButtons([...displayDropdownButtons]);
  };

  const antIcon = (
    <LoadingOutlined
      spin
      style={{ color: theme.colors.primary.primary, fontSize: 14 }}
    />
  );

  return (
    <TopbarWrapper showfilters={`${showFilters}`}>
      {LeftComponent && <div>{LeftComponent}</div>}
      <div>
        {onSearch && onSearchLoading && (
          <div className="TopbarInput">
            <SearchInput
              defaultValue={defaultValue}
              setSearch={onSearch}
              setSearchLoading={onSearchLoading}
              inputPlaceholder={inputPlaceholder}
              inputClassName={inputClassName ? inputClassName : null}
            />
          </div>
        )}
      </div>
      <div className="TopBarButtons">
        <span className="totalSelectedFiles">
          {totalSelectedElements
            ? `${totalSelectedElements} ${totalSelectedElements === 1 ? 'ARQUIVO' : 'ARQUIVOS'
            }`
            : ''}
        </span>
        {activityAIChat.enabled && activityAIChat.value === 'test' && toggleAiModal && (
          <AiButton onClick={toggleAiModal} />
        )}
        {buttons
          .filter((button) => !button.hidden)
          .map((button, index) => {
            const actionButton = () => {
              if (button.dropdownMenu) {
                if (!displayDropdownButtons[index]) {
                  toggleDropdown(index);
                }
              } else {
                if (button.action) {
                  button.action();
                }
              }
            };

            if (button.isLoading) {
              return (
                <Tooltip
                  key={`topbarTooltip${index}`}
                  overlay={button.tooltipMessage ?? ''}
                  placement="top"
                >
                  <TableActionTopbarButtonWrapper
                    disabled={`${button.disabled}`}
                    blocked={`${button.isBlocked}`}
                    key={`topbarButton${index}`}
                  >
                    <div
                      className={`TableActionTopbarButton ${displayDropdownButtons[index] ? 'active' : ''
                        }`}
                      onClick={button.isLoading ? undefined : actionButton}
                    >
                      {button.iconLoading && (
                        <SpinCustom size={8} color={theme.colors.primary.primary} marginRigth={8} />
                      )}
                      {!button.iconLoading && (
                        <>
                          {button.icon && !button.iconComponent && (
                            <Icon
                              color="primary"
                              icon={button.icon}
                              customSize={button.iconCustomSize}
                              disabled={button.disabled}
                              blocked={button.isBlocked}
                            />
                          )}
                          {!!button.iconComponent && !button.icon && button.iconComponent}
                        </>
                      )}
                      <span className="titleButton">{button.description}</span>
                      {button.showArrow && (
                        <Icon customColor={'#BEC2C6'} icon="expandir" customSize={10} />
                      )}
                    </div>
                    {button.dropdownMenu && displayDropdownButtons[index] && (
                      <DropdownActionTopbar
                        dropdownContent={button.dropdownMenu}
                        toggleDropdown={() => toggleDropdown(index)}
                      />
                    )}
                  </TableActionTopbarButtonWrapper>
                </Tooltip>
              );
            }

            return (
              <TableActionTopbarButtonWrapper
                disabled={`${button.disabled}`}
                blocked={`${button.isBlocked}`}
                key={`topbarButton${index}`}
              >
                <div
                  className={`TableActionTopbarButton ${displayDropdownButtons[index] ? 'active' : ''
                    }`}
                  onClick={actionButton}
                >
                  {button.iconLoading && (
                    <SpinCustom size={8} color={theme.colors.primary.primary} marginRigth={8} />
                  )}
                  {!button.iconLoading && (
                    <>
                      {button.icon && !button.iconComponent && (
                        <Icon
                          color="primary"
                          icon={button.icon}
                          customSize={button.iconCustomSize}
                          disabled={button.disabled}
                          blocked={button.isBlocked}
                        />
                      )}
                      {!!button.iconComponent && !button.icon && button.iconComponent}
                    </>
                  )}
                  <span className="titleButton">{button.description}</span>
                  {button.showArrow && (
                    <Icon customColor={'#BEC2C6'} icon="expandir" customSize={10} />
                  )}
                </div>
                {button.dropdownMenu && displayDropdownButtons[index] && (
                  <DropdownActionTopbar
                    dropdownContent={button.dropdownMenu}
                    toggleDropdown={() => toggleDropdown(index)}
                  />
                )}
              </TableActionTopbarButtonWrapper>
            );
          })}
      </div>
    </TopbarWrapper>
  );
};

export default TableActionTopbar;
